/**
 * useChat Hook
 * 
 * Real-time chat with:
 * - Message delivery/read receipts
 * - Typing indicators
 * - Offline queue
 * - Exact-once delivery guarantee
 */

import { useEffect, useRef, useCallback } from 'react';
import { wsClient, WS_CHANNELS } from '@/lib/ws';
import { useChatStore, ChatMessage, TypingIndicator } from '@/store/chat';
import { chatDb } from '@/lib/chatDb';
import { chatApi } from '@/lib/api';
import { logger } from '@/lib/logger';
import { useAuthStore } from '@/store/auth';

interface ChatEventPayload {
  message?: ChatMessage;
  messageId?: string;
  deliveredAt?: string;
  readAt?: string;
  typing?: TypingIndicator;
}

const TYPING_DEBOUNCE_MS = 1000;

export function useChat() {
  const user = useAuthStore((state) => state.user);
  const addMessage = useChatStore((state) => state.addMessage);
  const updateMessageStatus = useChatStore((state) => state.updateMessageStatus);
  const setTypingIndicator = useChatStore((state) => state.setTypingIndicator);
  const removeTypingIndicator = useChatStore((state) => state.removeTypingIndicator);
  const clearOldTypingIndicators = useChatStore((state) => state.clearOldTypingIndicators);
  
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const processedMessageIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Initialize IndexedDB
    chatDb.init();

    // Subscribe to chat events
    const unsubscribeNew = wsClient.subscribe(WS_CHANNELS.CHAT, async (payload: ChatEventPayload) => {
      if (payload.message) {
        await handleNewMessage(payload.message);
      }
    });

    const unsubscribeDelivered = wsClient.subscribe(WS_CHANNELS.CHAT_DELIVERED, (payload: ChatEventPayload) => {
      if (payload.messageId && payload.deliveredAt) {
        updateMessageStatus(payload.messageId, { deliveredAt: payload.deliveredAt });
      }
    });

    const unsubscribeRead = wsClient.subscribe(WS_CHANNELS.CHAT_READ, (payload: ChatEventPayload) => {
      if (payload.messageId && payload.readAt) {
        updateMessageStatus(payload.messageId, { readAt: payload.readAt });
      }
    });

    const unsubscribeTyping = wsClient.subscribe(WS_CHANNELS.CHAT_TYPING, (payload: ChatEventPayload) => {
      if (payload.typing && payload.typing.userId !== user?.id) {
        setTypingIndicator(payload.typing);
      }
    });

    // Clear old typing indicators periodically
    const typingInterval = setInterval(() => {
      clearOldTypingIndicators();
    }, 1000);

    // Process outbox on connect
    if (wsClient.isConnected()) {
      processOutbox();
    }

    const unsubscribeConnect = wsClient.onConnect(() => {
      processOutbox();
    });

    return () => {
      unsubscribeNew();
      unsubscribeDelivered();
      unsubscribeRead();
      unsubscribeTyping();
      unsubscribeConnect();
      clearInterval(typingInterval);
    };
  }, [user?.id]);

  const handleNewMessage = async (message: ChatMessage) => {
    // Exact-once delivery: Check if already processed
    if (processedMessageIds.current.has(message.id)) {
      logger.warn('Duplicate message prevented (already processed)', { messageId: message.id });
      return;
    }

    processedMessageIds.current.add(message.id);

    // Add to store
    addMessage(message);

    // Save to IndexedDB
    await chatDb.saveMessage(message);

    // Send delivered receipt (if not our own message)
    if (message.senderRole !== 'rider') {
      wsClient.send('chat-messages', 'delivered', {
        messageId: message.id,
        threadId: message.threadId,
      });
    }

    logger.info('Message received and stored', {
      messageId: message.id,
      threadId: message.threadId,
    });
  };

  const sendMessage = useCallback(async (threadId: string, messageText: string) => {
    if (!user) {
      logger.error('Cannot send message - no user', new Error('Not authenticated'));
      return;
    }

    const messageId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const timestamp = new Date().toISOString();

    const message: ChatMessage = {
      id: messageId,
      threadId,
      senderId: user.id,
      senderName: user.name,
      senderRole: 'rider',
      message: messageText,
      timestamp,
      localOnly: !wsClient.isConnected(),
    };

    // Add to store immediately (optimistic update)
    addMessage(message);

    // Save to IndexedDB
    await chatDb.saveMessage(message);

    // Try to send via WebSocket
    if (wsClient.isConnected()) {
      try {
        await chatApi.sendMessage(threadId, messageText);
        logger.info('Message sent successfully', { messageId, threadId });
      } catch (error) {
        logger.error('Failed to send message', error as Error, { messageId });
        
        // Add to outbox for retry
        await chatDb.addToOutbox({
          id: messageId,
          threadId,
          message: messageText,
          timestamp,
          retryCount: 0,
        });
      }
    } else {
      // Offline: Add to outbox
      await chatDb.addToOutbox({
        id: messageId,
        threadId,
        message: messageText,
        timestamp,
        retryCount: 0,
      });
      
      logger.info('Message queued for offline send', { messageId });
    }
  }, [user, addMessage]);

  const sendTypingIndicator = useCallback((threadId: string) => {
    if (!user || !wsClient.isConnected()) return;

    // Debounce typing indicator
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    wsClient.send('chat-messages', 'typing', {
      threadId,
      userId: user.id,
      userName: user.name,
      timestamp: Date.now(),
    });

    // Stop typing after debounce period
    typingTimeoutRef.current = setTimeout(() => {
      removeTypingIndicator(threadId, user.id);
    }, TYPING_DEBOUNCE_MS);
  }, [user, removeTypingIndicator]);

  const markAsRead = useCallback((threadId: string, messageId: string) => {
    if (!wsClient.isConnected()) return;

    wsClient.send('chat-messages', 'read', {
      threadId,
      messageId,
    });

    logger.debug('Message marked as read', { messageId, threadId });
  }, []);

  const processOutbox = async () => {
    const outboxItems = await chatDb.getOutboxItems();
    
    if (outboxItems.length === 0) return;

    logger.info('Processing outbox', { count: outboxItems.length });

    for (const item of outboxItems) {
      try {
        await chatApi.sendMessage(item.threadId, item.message);
        await chatDb.removeFromOutbox(item.id);
        logger.info('Outbox message sent', { messageId: item.id });
      } catch (error) {
        logger.error('Failed to send outbox message', error as Error, { messageId: item.id });
        
        // Increment retry count
        await chatDb.updateOutboxRetryCount(item.id, item.retryCount + 1);
        
        // Give up after 5 retries
        if (item.retryCount >= 5) {
          await chatDb.removeFromOutbox(item.id);
          logger.warn('Message failed after 5 retries, removed from outbox', { messageId: item.id });
        }
      }
    }
  };

  return {
    sendMessage,
    sendTypingIndicator,
    markAsRead,
  };
}


















